import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
} from "react";
import useWebSocketConnection from "../../hooks/useWebSocketConnection";
import { ReadyState } from "react-use-websocket";
import { useNavigate } from "react-router-dom";
import { 
  Block,
  JourneyOption,
  // Import WebSocket message types and event enum
  JourneyOptionsMessage,
  BlocksUpdateMessage,
  NavigationUpdateMessage,
  ErrorMessage,
  NavigationResult,
  WebSocketEventType,
  // Import the auto-generated union of all message types
  WebSocketMessage
} from '../../types/sprintLessonTypes';
import { useMultiPageLessonStore } from '../store/multiPageLessonStore';

// WebSocketEventType enum is now imported from sprintLessonTypes.ts
// We're exporting it again to maintain backward compatibility
export { WebSocketEventType };

// All message types and union type now come directly from the auto-generated types file
// We'll extend the imported WebSocketMessage to include a catch-all for any future message types
type ExtendedWebSocketMessage = WebSocketMessage | { event_type: string; [key: string]: any };

// Type for WebSocket message listeners
type WebSocketMessageListener = (message: ExtendedWebSocketMessage) => void;

// Type for WebSocket message subscription
type MessageSubscription = {
  id: string;
  filter?: (message: ExtendedWebSocketMessage) => boolean;
  callback: WebSocketMessageListener;
};

// Define types for our context
type SprintWebSocketContextType = {
  // WebSocket connection
  sendJsonMessage: (message: Record<string, any>) => void;
  lastJsonMessage: ExtendedWebSocketMessage | null;
  readyState: ReadyState;
  isConnected: boolean;
  reconnectWs: () => void;

  // Message subscription system
  subscribe: (
    filter: ((message: WebSocketMessage) => boolean) | undefined,
    callback: WebSocketMessageListener
  ) => string;
  unsubscribe: (subscriptionId: string) => void;
};

// Create the context with a default value
const SprintWebSocketContext = createContext<
  SprintWebSocketContextType | undefined
>(undefined);

type SprintWebSocketProviderProps = {
  children: React.ReactNode;
};

export const SprintWebSocketProvider: React.FC<
  SprintWebSocketProviderProps
> = ({ children }) => {
  // WebSocket setup
  const wsURL = `${window.location.protocol === "http:" ? "ws://" : "wss://"}${
    window.location.host
  }/ws/sprint/`;
  const { sendJsonMessage, lastJsonMessage, readyState, reconnectWs } =
    useWebSocketConnection({ wsURL });

  // Track all active subscriptions
  const subscriptionsRef = useRef<Map<string, MessageSubscription>>(new Map());

  // Process incoming WebSocket messages and notify subscribers
  useEffect(() => {
    if (!lastJsonMessage) return;

    console.log("Received WS message:");
    console.log(lastJsonMessage);

    // if (
    //   lastJsonMessage.event_type === "blocks_update" &&
    //   lastJsonMessage.blocks &&
    //   lastJsonMessage.blocks.length > 0 &&
    //   lastJsonMessage.blocks[0] &&
    //   lastJsonMessage.blocks[0].index_key != "ordered_blocks_0"
    // ) {
    //   console.log(
    //     "Received SPECIAL BLOCK....................*******************"
    //   );
    // }

    // Notify all subscribers whose filter matches the message
    subscriptionsRef.current.forEach((subscription) => {
      if (!subscription.filter || subscription.filter(lastJsonMessage)) {
        subscription.callback(lastJsonMessage);
      }
    });
  }, [lastJsonMessage]);

  // Subscription management
  const subscribe = useCallback(
    (
      filter: ((message: WebSocketMessage) => boolean) | undefined,
      callback: WebSocketMessageListener
    ) => {
      const id = crypto.randomUUID();
      subscriptionsRef.current.set(id, { id, filter, callback });
      return id;
    },
    []
  );

  const unsubscribe = useCallback((subscriptionId: string) => {
    subscriptionsRef.current.delete(subscriptionId);
  }, []);

  // Determine if connected
  const isConnected = readyState === ReadyState.OPEN;

  // Context value
  const contextValue: SprintWebSocketContextType = {
    // WebSocket connection
    sendJsonMessage,
    lastJsonMessage,
    readyState,
    isConnected,
    reconnectWs,

    // Subscription system
    subscribe,
    unsubscribe,
  };

  return (
    <SprintWebSocketContext.Provider value={contextValue}>
      {children}
    </SprintWebSocketContext.Provider>
  );
};

// Custom hook to use the WebSocket context
export const useSprintWebSocket = () => {
  const context = useContext(SprintWebSocketContext);

  if (context === undefined) {
    throw new Error(
      "useSprintWebSocket must be used within a SprintWebSocketProvider"
    );
  }

  return context;
};

// Custom hook to subscribe to WebSocket messages
export const useWebSocketSubscription = <T,>(
  filter?: (message: WebSocketMessage) => boolean,
  callback?: (message: T) => void
) => {
  const { subscribe, unsubscribe } = useSprintWebSocket();

  useEffect(() => {
    if (!callback) return;

    // Create the subscription
    const subscriptionId = subscribe(
      filter,
      callback as WebSocketMessageListener
    );

    // Clean up on unmount
    return () => {
      unsubscribe(subscriptionId);
    };
  }, [subscribe, unsubscribe, filter, callback]);
};

/**
 * Hook for subscribing to block updates.
 * This hook handles its own subscription lifecycle internally:
 * - Subscribes when the component mounts
 * - Updates subscription when dependencies change
 * - Automatically unsubscribes when the component unmounts
 * 
 * @param callback Function to call when blocks are received
 * @returns void - This hook does NOT return an unsubscribe function (handles cleanup internally)
 */
export const useBlocksSubscription = (callback: (blocks: Block[]) => void): void => {
  // Tracking block sequences to prevent out-of-order updates
  const blockSequences = useRef<Record<string, number>>({});
  
  // WebSocket context for subscribe/unsubscribe
  const { subscribe, unsubscribe } = useSprintWebSocket();
  
  // Message filter - only process block update messages
  const messageFilter = useCallback(
    (message: WebSocketMessage) => message.event_type === WebSocketEventType.BLOCKS_UPDATE, 
    []
  );
  
  // Setup the subscription
  useEffect(() => {
    // Reference to track if component is still mounted
    let isMounted = true;
    
    // Message handler function
    const handleMessage = (message: BlocksUpdateMessage) => {
      if (!isMounted) return;
      
      // Process lesson schema if present
      if (message.lesson_schema) {
        try {
          console.log('Received lesson schema');
          const parsedSchema = JSON.parse(message.lesson_schema);
          useMultiPageLessonStore.getState().setLessonSchema(parsedSchema);
        } catch (error) {
          console.error('Failed to parse lesson schema:', error);
        }
      }
      
      // Get message sequence number
      const messageSequence = message.sequence || 0;
      
      // Filter blocks to prevent out-of-order updates
      const filteredBlocks = message.blocks.filter(block => {
        const blockKey = block.index_key;
        const blockSequence = (block as any).sequence || messageSequence;
        const lastSeenSequence = blockSequences.current[blockKey] || 0;
        
        if (blockSequence > lastSeenSequence) {
          blockSequences.current[blockKey] = blockSequence;
          return true;
        }
        return false;
      });
      
      // Skip if no new blocks
      if (filteredBlocks.length === 0) return;
      
      // Process blocks through the multi-page lesson store
      // We'll extract the page container from each block's index_key
      if (filteredBlocks.length > 0) {
        const store = useMultiPageLessonStore.getState();
        const schemaProps = store.lessonSchema?.properties;
        
        if (schemaProps) {
          // Group blocks by page based on their index_key
          const blocksByPage = new Map<number, Block[]>();
          
          filteredBlocks.forEach(block => {
            // Extract the container name from the index_key (everything before the first dot)
            const containerPath = block.index_key.split('.')[0];
            const orderedContainers = Object.keys(schemaProps);
            const pageIndex = orderedContainers.indexOf(containerPath);
            
            if (pageIndex !== -1) {
              // Initialize the array if this is the first block for this page
              if (!blocksByPage.has(pageIndex)) {
                blocksByPage.set(pageIndex, []);
              }
              
              // Add the block to its page group
              blocksByPage.get(pageIndex)!.push(block);
            } else {
              console.warn(`Unknown page container in index_key: ${containerPath}`);
            }
          });
          
          // Update each page with its blocks - no stale detection
          blocksByPage.forEach((pageBlocks, pageIndex) => {
            console.log(`Updating page ${pageIndex} with ${pageBlocks.length} blocks`);
            
            // Simply update the blocks without removing any
            // This eliminates the race condition during streaming
            store.updatePageBlocks(pageIndex, pageBlocks);
          });
        }
      }
      
      // Call the provided callback with filtered blocks
      callback(filteredBlocks);
    };
    
    // Create the subscription
    const subscriptionId = subscribe(
      messageFilter, 
      handleMessage as WebSocketMessageListener
    );
    
    // Cleanup function
    return () => {
      isMounted = false;
      unsubscribe(subscriptionId);
    };
  }, [subscribe, unsubscribe, messageFilter, callback, blockSequences]);
};

export const useNavigationResult = (
  callback: (result: {
    view?: string;
    journey_id?: string;
    lesson_id?: string;
  }) => void
) => {
  useWebSocketSubscription(
    (message) => message.event_type === WebSocketEventType.NAVIGATION_UPDATE,
    (message) => callback(message.navigation_result)
  );
};

// Generic event type subscription hook
export const useEventType = <T,>(
  eventType: WebSocketEventType,
  callback: (data: T) => void
) => {
  useWebSocketSubscription(
    (message) => message.event_type === eventType,
    (message) => callback(message)
  );
};
